import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User } from '../../../models/user.model';
import { UserService } from '../../../services/user.service';
import { ToastrService } from 'ngx-toastr';
import { RoleService } from '../../../services/role.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  users: User[] = [];
  selectedUser: User | null = null;
  originalUserData: User | null = null; // Store original data for cancel functionality
  userForm: FormGroup;
  isLoading = false;
  isEditing = false;
  allPossibleRoles = ['ROLE_EMPLOYEE', 'ROLE_MANAGER', 'ROLE_UNIONADMIN', 'ROLE_SUPERADMIN'];
  availableRoles: string[] = [];
  showPasswordField = true;
  isSuperAdmin = false;
  currentUserId: number | null = null;

  constructor(
    private userService: UserService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private roleService: RoleService
  ) {
    // Check user roles
    this.isSuperAdmin = this.roleService.hasRole('ROLE_SUPERADMIN');
    
    // Set available roles based on current user's role
    if (this.isSuperAdmin) {
      // Super admin can create any role
      this.availableRoles = [...this.allPossibleRoles];
    } else {
      // Manager can create employee, union admin, and other managers, but not superadmins
      this.availableRoles = ['ROLE_EMPLOYEE', 'ROLE_UNIONADMIN', 'ROLE_MANAGER'];
    }
    
    this.userForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      roles: [['ROLE_EMPLOYEE'], Validators.required]
    });
  }

  ngOnInit(): void {
    this.getCurrentUser();
    this.loadUsers();
  }

  // Get current logged-in user information
  getCurrentUser(): void {
    const userDataStr = localStorage.getItem('user-data');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        this.currentUserId = userData.id;
      } catch (error) {
        console.error('Error parsing current user data:', error);
      }
    }
  }

  loadUsers(): void {
    this.isLoading = true;
    this.userService.getUsers().subscribe(
      (data) => {
        // If not a super admin, filter out superadmin users
        if (!this.isSuperAdmin) {
          data = data.filter((user: User) => 
            !user.roles?.includes('ROLE_SUPERADMIN')
          );
        }
        this.users = data;
        this.isLoading = false;
      },
      (error) => {
        this.toastr.error('Failed to load users', 'Error');
        console.error('Error loading users:', error);
        this.isLoading = false;
      }
    );
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      return;
    }

    const userData = this.userForm.value;
    
    // Filter out roles that the current user can't assign
    if (!this.isSuperAdmin) {
      userData.roles = userData.roles.filter((role: string) => 
        role !== 'ROLE_SUPERADMIN' // Only restrict superadmin role
      );
      
      // Ensure at least one role is assigned
      if (userData.roles.length === 0) {
        userData.roles = ['ROLE_EMPLOYEE'];
      }
    }
    
    if (this.isEditing && this.selectedUser) {
      // If password field is empty during edit, remove it from the payload
      if (!userData.password) {
        delete userData.password;
      }
      
      // Check if trying to edit a superadmin
      if (!this.isSuperAdmin && this.selectedUser.roles?.includes('ROLE_SUPERADMIN')) {
        this.toastr.error('You cannot edit a Super Admin user', 'Permission Denied');
        return;
      }
      
      this.userService.updateUser(this.selectedUser.id!, userData).subscribe(
        () => {
          this.toastr.success('User updated successfully', 'Success');
          this.resetForm();
          this.loadUsers();
        },
        (error) => {
          this.toastr.error('Failed to update user', 'Error');
          console.error('Error updating user:', error);
        }
      );
    } else {
      this.userService.createUser(userData).subscribe(
        () => {
          this.toastr.success('User created successfully', 'Success');
          this.resetForm();
          this.loadUsers();
        },
        (error) => {
          this.toastr.error('Failed to create user', 'Error');
          console.error('Error creating user:', error);
        }
      );
    }
  }

  editUser(user: User): void {
    // Prevent users from editing their own account (especially roles)
    if (this.isCurrentUser(user)) {
      this.toastr.error('আপনি নিজের একাউন্ট সম্পাদনা করতে পারবেন না', 'অনুমতি নেই');
      return;
    }

    // Check if user is a superadmin and current user is not
    if (!this.isSuperAdmin && user.roles?.includes('ROLE_SUPERADMIN')) {
      this.toastr.error('You cannot edit a Super Admin user', 'Permission Denied');
      return;
    }
    
    // Store original user data for cancel functionality
    this.originalUserData = JSON.parse(JSON.stringify(user)); // Deep copy
    this.selectedUser = user;
    this.isEditing = true;
    this.showPasswordField = false;
    
    // Remove password validation when editing
    this.userForm.get('password')?.clearValidators();
    this.userForm.get('password')?.setValue('');
    
    this.userForm.patchValue({
      username: user.username,
      email: user.email,
      roles: user.roles || ['ROLE_EMPLOYEE']
    });
  }

  deleteUser(user: User): void {
    // Check if user is trying to delete themselves
    if (this.currentUserId && user.id === this.currentUserId) {
      this.toastr.error('আপনি নিজেকে ডিলিট করতে পারবেন না', 'অনুমতি নেই');
      return;
    }

    // Check if user is a superadmin and current user is not
    if (!this.isSuperAdmin && user.roles?.includes('ROLE_SUPERADMIN')) {
      this.toastr.error('You cannot delete a Super Admin user', 'Permission Denied');
      return;
    }
    
    if (confirm(`Are you sure you want to delete user ${user.username}?`)) {
      this.userService.deleteUser(user.id!).subscribe(
        () => {
          this.toastr.success('User deleted successfully', 'Success');
          this.loadUsers();
        },
        (error) => {
          this.toastr.error('Failed to delete user', 'Error');
          console.error('Error deleting user:', error);
        }
      );
    }
  }

  resetForm(): void {
    this.userForm.reset({
      roles: ['ROLE_EMPLOYEE']
    });
    this.selectedUser = null;
    this.originalUserData = null;
    this.isEditing = false;
    this.showPasswordField = true;
    
    // Restore password validation for new user
    this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    this.userForm.get('password')?.setValue('');
  }
  
  cancelEdit(): void {
    // Check if form has been modified
    const formData = this.userForm.value;
    const hasChanges = this.originalUserData && (
      formData.username !== this.originalUserData.username ||
      formData.email !== this.originalUserData.email ||
      JSON.stringify(formData.roles) !== JSON.stringify(this.originalUserData.roles)
    );
    
    // Show confirmation if there are unsaved changes
    if (hasChanges) {
      const confirmed = confirm('আপনার পরিবর্তনগুলি সংরক্ষিত হবে না। আপনি কি নিশ্চিত যে আপনি বাতিল করতে চান?');
      if (!confirmed) {
        return; // Don't cancel if user chooses to keep editing
      }
    }
    
    // Restore original user data if we were editing
    if (this.isEditing && this.selectedUser && this.originalUserData) {
      // Find the user in the array and restore original data
      const userIndex = this.users.findIndex(u => u.id === this.selectedUser!.id);
      if (userIndex !== -1) {
        this.users[userIndex] = JSON.parse(JSON.stringify(this.originalUserData)); // Deep copy back
      }
    }
    
    this.resetForm();
    this.toastr.info('সম্পাদনা বাতিল করা হয়েছে', 'বাতিল');
  }
  
  // Check if user has a specific role
  hasRole(user: User, role: string): boolean {
    return user.roles ? user.roles.includes(role) : false;
  }

  // Method for handling role checkbox changes in the form
  updateRoleSelection(role: string): void {
    const rolesControl = this.userForm.get('roles');
    const currentRoles = rolesControl?.value as string[] || [];
    
    if (currentRoles.includes(role)) {
      // Remove role if already selected, but ensure at least one role remains
      if (currentRoles.length > 1) {
        const index = currentRoles.indexOf(role);
        currentRoles.splice(index, 1);
      }
    } else {
      // Add role if not selected
      currentRoles.push(role);
    }
    
    rolesControl?.setValue(currentRoles);
  }

  // Method to check if the current user can assign a specific role
  canAssignRole(role: string): boolean {
    if (this.isSuperAdmin) {
      return true;
    }
    
    // Manager can assign any role except superadmin
    return role !== 'ROLE_SUPERADMIN';
  }

  // Method to toggle a role for a user
  toggleRole(user: User, role: string): void {
    // Prevent users from changing their own roles
    if (this.isCurrentUser(user)) {
      this.toastr.error('আপনি নিজের ভূমিকা পরিবর্তন করতে পারবেন না', 'অনুমতি নেই');
      return;
    }

    // Prevent role changes when this user is being edited in the form
    if (this.isEditing && this.selectedUser?.id === user.id) {
      this.toastr.warning('Cannot change roles while editing user. Please use the form or cancel editing first.', 'Warning');
      return;
    }
    
    // Check if the current user can assign this role
    if (!this.canAssignRole(role)) {
      this.toastr.error(`You don't have permission to assign the ${role} role`, 'Permission Denied');
      return;
    }
    
    if (!user.roles) {
      user.roles = [];
    }
    
    const index = user.roles.indexOf(role);
    if (index > -1) {
      // Don't allow removing the last role
      if (user.roles.length <= 1) {
        this.toastr.warning('User must have at least one role', 'Warning');
        return;
      }
      user.roles.splice(index, 1);
    } else {
      user.roles.push(role);
    }
    
    this.userService.updateUserRoles(user.id!, user.roles).subscribe(
      () => {
        this.toastr.success(`Role ${role} updated for ${user.username}`, 'Success');
      },
      (error) => {
        this.toastr.error(`Failed to update role for ${user.username}`, 'Error');
        console.error('Error updating roles:', error);
        // Revert the change in the UI
        this.loadUsers();
      }
    );
  }

  // Check if the given user is the current logged-in user
  isCurrentUser(user: User): boolean {
    return this.currentUserId !== null && user.id === this.currentUserId;
  }
}
